import * as THREE from 'three';
import { generateUniqueId } from './generateUniqueId';

/**
 * Wire Bundling — Ctrl+B + drag-select ile seçilen wire'ları takip ederek
 * tüm paralel stretch boyunca bundle'lar. Kurallar:
 *   1. Bundle, seçim rect'inden itibaren her iki yöne (forward + backward)
 *      uzar.
 *   2. Wire'lar aralarındaki spacing'i koruyarak aynı segment direction'larını
 *      takip ederse devam eder. Birisi ayrılırsa bundle orada durur.
 *   3. Bir wire'da komponent varsa (vertex'te açılı kırılma, ve bu kırılma
 *      diğer wire'lar tarafından paylaşılmıyorsa) bundle durur.
 *   4. Bundle'lanan wire kısmı GÖRÜNMEZ olur (geometriden çıkarılır).
 *   5. Temsil çizgisi bundle yolunun ortalarından geçer (turn'ları takip eder).
 *
 * Scene-based + redux'a dokunmaz. Expand orijinal geometriyi geri yükler.
 */

const WIRE_TYPES_PATTERN = /wire/i;
const COLLECTOR_SPACING = 3;
const COLLECTOR_COUNT = 3;
const COLLECTOR_PAD = 2;
const ANGLE_TOL_DEG = 5;
const STUB_LEN = 20; // Bundle başlangıcında ve sonunda görünür bırakılan wire uzunluğu
const BUNDLE_COLOR_COLLECTOR = 0xff00ff;
const BUNDLE_COLOR_REPRESENT = 0xff0000;

// ─── Filters & helpers ───────────────────────────────────────────────────────

function isBundleableWire(obj) {
  if (!obj || !obj.isLine) return false;
  const ud = obj.userData || {};
  // Bundle rep line wire-benzeri davranır → bundleable (bundle-of-bundles için).
  // isBundlePart filter'ını öncelikle bypass et.
  if (ud.isBundleRep) return true;
  if (ud.isPanelPart) return false;
  if (ud.type === 'wireTitle') return false;
  if (ud.lineType === 'frontEdge') return false;
  if (ud.isBundlePart) return false;
  if (ud.isTransient) return false;
  if (ud.isOffsetPreview) return false;
  if (ud.role === 'wireMarker') return false;
  if (ud.type && /wire/i.test(ud.type)) return true;
  if (ud.feederId || ud.wireId || ud.wireID) return true;
  if (ud.panelID) return true;
  if (ud.function && /wire/i.test(ud.function)) return true;
  return false;
}

function cloneGeometryPositions(wire) {
  const pos = wire.geometry?.attributes?.position;
  if (!pos) return [];
  const out = [];
  for (let i = 0; i < pos.count; i++) out.push(pos.getX(i), pos.getY(i), pos.getZ(i));
  return out;
}

function getWorldPts(wire) {
  const pos = wire.geometry?.attributes?.position;
  if (!pos || pos.count < 2) return [];
  wire.updateMatrixWorld(true);
  const pts = [];
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.set(pos.getX(i), pos.getY(i), 0).applyMatrix4(wire.matrixWorld);
    pts.push({ x: v.x, y: v.y });
  }
  return pts;
}

function dist(a, b) { return Math.hypot(b.x - a.x, b.y - a.y); }

function segDir(a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const l = Math.hypot(dx, dy);
  if (l < 1e-6) return { x: 0, y: 0, angle: 0 };
  let angle = Math.atan2(dy, dx) * 180 / Math.PI;
  if (angle < 0) angle += 360;
  return { x: dx / l, y: dy / l, angle };
}

function dirsMatch(d1, d2, tolDeg = ANGLE_TOL_DEG) {
  let diff = Math.abs(d1.angle - d2.angle);
  if (diff > 180) diff = 360 - diff;
  return diff < tolDeg;
}

// Liang-Barsky rect clip
function clipSegmentToRect(a, b, rect) {
  const tMin = [0], tMax = [1];
  const dx = b.x - a.x, dy = b.y - a.y;
  const updateT = (p, q) => {
    if (Math.abs(p) < 1e-9) return q >= 0;
    const t = q / p;
    if (p < 0) { if (t > tMax[0]) return false; if (t > tMin[0]) tMin[0] = t; }
    else       { if (t < tMin[0]) return false; if (t < tMax[0]) tMax[0] = t; }
    return true;
  };
  if (!updateT(-dx, a.x - rect.xMin)) return null;
  if (!updateT( dx, rect.xMax - a.x)) return null;
  if (!updateT(-dy, a.y - rect.yMin)) return null;
  if (!updateT( dy, rect.yMax - a.y)) return null;
  if (tMax[0] <= tMin[0]) return null;
  return {
    tIn: tMin[0], tOut: tMax[0],
    in:  { x: a.x + dx * tMin[0], y: a.y + dy * tMin[0] },
    out: { x: a.x + dx * tMax[0], y: a.y + dy * tMax[0] },
  };
}

// İki segment kesişimi. a1-a2 ve b1-b2 doğru parçalarının kesişme noktasını
// döner; kesişmiyorsa null. {t, u, point} — t: a-segment parametresi (0..1),
// u: b-segment parametresi.
function segIntersect(a1, a2, b1, b2) {
  const dx1 = a2.x - a1.x, dy1 = a2.y - a1.y;
  const dx2 = b2.x - b1.x, dy2 = b2.y - b1.y;
  const denom = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(denom) < 1e-9) return null; // paralel
  const t = ((b1.x - a1.x) * dy2 - (b1.y - a1.y) * dx2) / denom;
  const u = ((b1.x - a1.x) * dy1 - (b1.y - a1.y) * dx1) / denom;
  if (t < -0.001 || t > 1.001 || u < -0.001 || u > 1.001) return null;
  const ct = Math.max(0, Math.min(1, t));
  return { t: ct, u, point: { x: a1.x + dx1 * ct, y: a1.y + dy1 * ct } };
}

// Wire polyline'ı ile slice (iki nokta tanımlı doğru parçası) kesişimi.
// {idx, t, point} — wire'ın hangi segmentinde, segment içi parametre, dünya noktası.
function findWireSliceIntersection(pts, sA, sB) {
  for (let i = 0; i < pts.length - 1; i++) {
    const inter = segIntersect(pts[i], pts[i + 1], sA, sB);
    if (inter) return { idx: i, t: inter.t, point: inter.point };
  }
  return null;
}

// Bir noktanın polyline üzerine en yakın projeksiyonu — {idx, t, point, dist}.
// idx: pts[idx]→pts[idx+1] segmentine düştüğü segment index'i.
function projectOnPolyline(pts, p) {
  let best = null;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-9) continue;
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const px = a.x + dx * t, py = a.y + dy * t;
    const d = Math.hypot(p.x - px, p.y - py);
    if (!best || d < best.dist) {
      best = { idx: i, t, point: { x: px, y: py }, dist: d };
    }
  }
  return best;
}

// İki tıklama noktasından, rect-tabanlı kodun beklediği `{pts, enterIdx,
// exitIdx, enterPoint, exitPoint, entryDir}` yapısını üretir. Böylece
// truncate/midline/collector fonksiyonları olduğu gibi tekrar kullanılabilir.
function findWireCrossingFromTwoPoints(wire, pA, pB) {
  const pts = getWorldPts(wire);
  if (pts.length < 2) return null;
  const projA = projectOnPolyline(pts, pA);
  const projB = projectOnPolyline(pts, pB);
  if (!projA || !projB) return null;

  // Wire üzerinde hangi projeksiyon önce gelir? (arc parametresi: idx + t)
  const arcA = projA.idx + projA.t;
  const arcB = projB.idx + projB.t;
  const enterProj = arcA <= arcB ? projA : projB;
  const exitProj  = arcA <= arcB ? projB : projA;

  if (dist(enterProj.point, exitProj.point) < 1) return null;

  const enterIdx = enterProj.idx;
  const exitIdx  = exitProj.idx;
  const enterPoint = enterProj.point;
  const exitPoint  = exitProj.point;
  const dirEnd = (enterIdx === exitIdx) ? exitPoint : pts[enterIdx + 1];
  const entryDir = segDir(enterPoint, dirEnd);

  return { pts, enterIdx, exitIdx, enterPoint, exitPoint, entryDir };
}

// İki "slice" hattından (kullanıcının çizdiği BİRLEŞTİRME ve AÇMA hatları)
// crossing-benzeri yapı üretir. Wire her iki slice'ı da geçmek zorundadır.
function findWireCrossingFromSlices(wire, mergeA, mergeB, openA, openB) {
  const pts = getWorldPts(wire);
  if (pts.length < 2) return null;
  const mergeHit = findWireSliceIntersection(pts, mergeA, mergeB);
  const openHit  = findWireSliceIntersection(pts, openA, openB);
  if (!mergeHit || !openHit) return null;

  // Wire üzerinde hangi kesişim önce gelir? (arc parametresi: idx + t)
  const arcMerge = mergeHit.idx + mergeHit.t;
  const arcOpen  = openHit.idx  + openHit.t;
  const enterHit = arcMerge <= arcOpen ? mergeHit : openHit;
  const exitHit  = arcMerge <= arcOpen ? openHit  : mergeHit;

  if (dist(enterHit.point, exitHit.point) < 1) return null;

  const enterIdx = enterHit.idx;
  const exitIdx  = exitHit.idx;
  const enterPoint = enterHit.point;
  const exitPoint  = exitHit.point;
  const dirEnd = (enterIdx === exitIdx) ? exitPoint : pts[enterIdx + 1];
  const entryDir = segDir(enterPoint, dirEnd);

  return { pts, enterIdx, exitIdx, enterPoint, exitPoint, entryDir };
}

// Wire'ın rect ile giriş ve çıkış segmentlerini bul
function findWireRectCrossing(wire, rect) {
  const pts = getWorldPts(wire);
  if (pts.length < 2) return null;

  let enterIdx = -1, enterPoint = null;
  let exitIdx = -1, exitPoint = null;

  for (let i = 0; i < pts.length - 1; i++) {
    const clip = clipSegmentToRect(pts[i], pts[i + 1], rect);
    if (!clip) continue;
    if (enterIdx === -1) { enterIdx = i; enterPoint = clip.in; }
    exitIdx = i; exitPoint = clip.out;
  }
  if (enterIdx === -1) return null;

  const entryDir = segDir(pts[enterIdx], pts[enterIdx + 1]);
  return { pts, enterIdx, exitIdx, enterPoint, exitPoint, entryDir };
}

// Forward segment listesi: exitPoint'ten wire sonuna kadar
// Her segment: { start, end, dir }
function getForwardSegs(crossing) {
  const { pts, exitIdx, exitPoint } = crossing;
  const segs = [];
  // Segment 0: exitPoint → pts[exitIdx+1]
  if (dist(exitPoint, pts[exitIdx + 1]) > 1e-3) {
    segs.push({
      start: { x: exitPoint.x, y: exitPoint.y },
      end: { x: pts[exitIdx + 1].x, y: pts[exitIdx + 1].y },
      dir: segDir(exitPoint, pts[exitIdx + 1]),
      endVertexIdx: exitIdx + 1,
    });
  }
  for (let i = exitIdx + 1; i < pts.length - 1; i++) {
    segs.push({
      start: { x: pts[i].x, y: pts[i].y },
      end: { x: pts[i + 1].x, y: pts[i + 1].y },
      dir: segDir(pts[i], pts[i + 1]),
      endVertexIdx: i + 1,
    });
  }
  return segs;
}

// Backward segment listesi: enterPoint'ten wire başına doğru (reversed)
function getBackwardSegs(crossing) {
  const { pts, enterIdx, enterPoint } = crossing;
  const segs = [];
  if (dist(enterPoint, pts[enterIdx]) > 1e-3) {
    segs.push({
      start: { x: enterPoint.x, y: enterPoint.y },
      end: { x: pts[enterIdx].x, y: pts[enterIdx].y },
      dir: segDir(enterPoint, pts[enterIdx]),
      endVertexIdx: enterIdx,
    });
  }
  for (let i = enterIdx; i > 0; i--) {
    segs.push({
      start: { x: pts[i].x, y: pts[i].y },
      end: { x: pts[i - 1].x, y: pts[i - 1].y },
      dir: segDir(pts[i], pts[i - 1]),
      endVertexIdx: i - 1,
    });
  }
  return segs;
}

// enterPoint'ten başlayıp wire'ın POLYLINE'ı boyunca `distance` kadar yürü.
// Eski sürüm enterPoint→exitPoint arasını tek düz segment varsayıyordu — bu
// rect-flow için (rect tek bir segmenti kestiğinde) doğru ama slice-flow'da
// iki kesim arasında köşeli yol olabileceği için yanlış. Bu sürüm enterIdx
// ile exitIdx arasındaki tüm köşelerden geçer, sonra exitPoint sonrasındaki
// wire kalan kısmından devam eder.
function walkForwardFromPoint(crossing, distance) {
  const { enterPoint, exitPoint, pts, enterIdx, exitIdx } = crossing;
  const path = [];
  let remaining = distance;
  let current = { x: enterPoint.x, y: enterPoint.y };

  // Bir noktaya kadar düz git; pay yetiyorsa orada dur, yetmiyorsa noktaya kadar git.
  const stepTo = (target) => {
    const segLen = dist(current, target);
    if (segLen < 1e-9) { current = target; return false; }
    if (segLen >= remaining) {
      const frac = remaining / segLen;
      path.push({
        x: current.x + (target.x - current.x) * frac,
        y: current.y + (target.y - current.y) * frac,
      });
      remaining = 0;
      return true; // bittik
    }
    path.push({ x: target.x, y: target.y });
    remaining -= segLen;
    current = { x: target.x, y: target.y };
    return false;
  };

  // 1) enterPoint → exitPoint: enterIdx ile exitIdx arasındaki tüm köşelerden geç
  if (enterIdx === exitIdx) {
    if (stepTo(exitPoint)) return path;
  } else {
    if (stepTo(pts[enterIdx + 1])) return path;
    for (let i = enterIdx + 2; i <= exitIdx; i++) {
      if (stepTo(pts[i])) return path;
    }
    if (stepTo(exitPoint)) return path;
  }
  // 2) exitPoint sonrası wire'ın kalan kısmı boyunca devam
  for (let i = exitIdx + 1; i < pts.length && remaining > 0; i++) {
    if (stepTo(pts[i])) return path;
  }
  return path;
}

// exitPoint'ten geriye doğru wire'ın POLYLINE'ı boyunca `distance` yürü.
// Path'i forward order'da döner.
function walkBackwardFromPoint(crossing, distance) {
  const { enterPoint, exitPoint, pts, enterIdx, exitIdx } = crossing;
  const reversed = [];
  let remaining = distance;
  let current = { x: exitPoint.x, y: exitPoint.y };

  const stepTo = (target) => {
    const segLen = dist(current, target);
    if (segLen < 1e-9) { current = target; return false; }
    if (segLen >= remaining) {
      const frac = remaining / segLen;
      reversed.push({
        x: current.x + (target.x - current.x) * frac,
        y: current.y + (target.y - current.y) * frac,
      });
      remaining = 0;
      return true;
    }
    reversed.push({ x: target.x, y: target.y });
    remaining -= segLen;
    current = { x: target.x, y: target.y };
    return false;
  };

  // 1) exitPoint → enterPoint: ters yönde köşelerden geç
  if (enterIdx === exitIdx) {
    if (stepTo(enterPoint)) return reversed.reverse();
  } else {
    if (stepTo(pts[exitIdx])) return reversed.reverse();
    for (let i = exitIdx - 1; i > enterIdx; i--) {
      if (stepTo(pts[i])) return reversed.reverse();
    }
    if (stepTo(enterPoint)) return reversed.reverse();
  }
  // 2) enterPoint sonrası wire'ın başına doğru devam
  for (let i = enterIdx; i > 0 && remaining > 0; i--) {
    if (stepTo(pts[i - 1])) return reversed.reverse();
  }
  return reversed.reverse();
}

// Lockstep walk: segment listlerini senkron taramayı dener, tüm wire'ların
// segmentleri aynı yönde olduğu sürece adım sayısını artırır.
function lockstepWalk(segsArray) {
  const minLen = Math.min(...segsArray.map(s => s.length));
  let steps = 0;
  for (let step = 0; step < minLen; step++) {
    const refDir = segsArray[0][step].dir;
    const allMatch = segsArray.every(segs => dirsMatch(segs[step].dir, refDir));
    if (!allMatch) break;
    steps++;
  }
  return steps;
}

// Bir wire üzerindeki komponentleri (buat, socket, lighting, switch) bul.
// Komponentler bundle'ı durdurur — bir komponenten hemen öncesinde (stub dahil)
// bundle biter ve collector oluşur.
const COMPONENT_TYPES = new Set([
  'socketBuat', 'lightingBuat', 'buatCircle',
  'socket', 'lighting', 'lightSwitch',
  'fixture', 'machine', 'bellButton', 'bellTrafo', 'grounding',
]);
function findComponentsOnWire(scene, wire) {
  const wireId = wire.userData?.id;
  if (!wireId) return [];
  const results = [];
  scene.traverse(obj => {
    const u = obj.userData;
    if (!u) return;
    if (!COMPONENT_TYPES.has(u.type)) return;
    const wid = u.wireID || u.wireId || u.parentWireId;
    if (wid !== wireId) return;
    const wp = new THREE.Vector3();
    obj.getWorldPosition(wp);
    results.push({ obj, pos: { x: wp.x, y: wp.y } });
  });
  return results;
}

// Bir polyline üzerinde en yakın vertex indexi ve o vertex'e dik mesafesi.
function nearestWireVertexIdx(pts, p) {
  let bestI = -1;
  let bestD = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const d = Math.hypot(pts[i].x - p.x, pts[i].y - p.y);
    if (d < bestD) { bestD = d; bestI = i; }
  }
  return { idx: bestI, dist: bestD };
}

// forwSegs/backSegs içinde endVertexIdx verilen vertex'e eşit olan ilk step'i bul.
// Bulunursa step sayısı (k+1) — yani bundle'ın forward extreme'i tam o vertex
// olur — döner; bulunamazsa Infinity.
function stepsAtVertex(segs, vertexIdx) {
  for (let k = 0; k < segs.length; k++) {
    if (segs[k].endVertexIdx === vertexIdx) return k + 1;
  }
  return Infinity;
}

// Lockstep'ten gelen fwd/bwd step sayılarını, bundle kapsamındaki wire'ların
// üzerindeki komponentlere (buat/priz/lamba/anahtar) göre kıstır. Amaç: bundle
// kesinlikle bir komponentten ÖNCE bitsin; stub görünür kısım da komponentten
// önceki 20 birim olsun.
function limitStepsByComponents(scene, enriched, fwdSteps, bwdSteps) {
  const VERTEX_TOL = 5; // birim; bu mesafeden uzaksa vertex ile ilişkilendirmiyoruz
  let maxFwd = fwdSteps;
  let maxBwd = bwdSteps;
  for (const e of enriched) {
    const comps = findComponentsOnWire(scene, e.wire);
    if (comps.length === 0) continue;
    const pts = e.crossing.pts;
    for (const c of comps) {
      const { idx, dist } = nearestWireVertexIdx(pts, c.pos);
      if (idx < 0 || dist > VERTEX_TOL) continue;
      // Forward: komponent vertex forward yönünde ise fwdSteps kısıtla
      const fwdK = stepsAtVertex(e.forwSegs, idx);
      if (fwdK < maxFwd) maxFwd = fwdK;
      // Backward: komponent vertex backward yönünde ise bwdSteps kısıtla
      const bwdK = stepsAtVertex(e.backSegs, idx);
      if (bwdK < maxBwd) maxBwd = bwdK;
    }
  }
  return { fwdSteps: Math.max(0, maxFwd), bwdSteps: Math.max(0, maxBwd) };
}

// Bundle'a dahil wire'ların panel exit index aralığını hesapla.
// panel.userData.outs sırası exit point index'iyle eşleşir (outs[i] → exitIndex i).
// wireIds verilen wire'ların outs içindeki index'lerinin min/max'ı bundle'ın
// panel-side aralığını verir. Bundle bir panele bağlı değilse null döner.
//
// Bu aralık iki amaca hizmet eder:
//   1. useGizmo swap logic'i: tek wire bu aralığa swap etmesin (Phase 2).
//   2. useWireBundling: bundle entry collector cluster click+drag (Phase 3).
export function computeBundlePanelExitRange(scene, wireIds) {
  if (!Array.isArray(wireIds) || wireIds.length === 0) return null;
  const wireIdSet = new Set(wireIds.map(String));
  // Her bundle wire'ı için panel ID'sini ve outs index'ini bul. Bundle birden
  // fazla panele yayılıyorsa hesap karışır → ilk bulunan panele göre.
  let panelID = null;
  const indices = [];
  scene.traverse(obj => {
    if (panelID && indices.length === wireIds.length) return; // erken çık
    if (!obj.isLine || !obj.userData) return;
    const wid = obj.userData.id != null ? String(obj.userData.id) : null;
    if (!wid || !wireIdSet.has(wid)) return;
    const wirePanelID = obj.userData.panelID;
    if (!wirePanelID) return;
    if (!panelID) panelID = wirePanelID;
    if (wirePanelID !== panelID) return;
  });
  if (!panelID) return null;

  // Paneli bul, outs'tan index'leri çıkar
  let panel = null;
  scene.traverse(obj => {
    if (panel) return;
    if (obj.userData?.id === panelID && Array.isArray(obj.userData?.outs)) panel = obj;
  });
  if (!panel) return null;
  const outs = panel.userData.outs || [];
  for (let i = 0; i < outs.length; i++) {
    const out = outs[i];
    if (!out) continue;
    const candidates = [
      out.wireId,
      out.wires?.[0]?.id,
      out.wires?.[0],
      out.feederId,
      out.loadId,
    ].filter(v => v != null).map(String);
    if (candidates.some(c => wireIdSet.has(c))) {
      indices.push(i);
    }
  }
  if (indices.length === 0) return null;
  return {
    panelID,
    min: Math.min(...indices),
    max: Math.max(...indices),
    indices: indices.slice().sort((a, b) => a - b),
  };
}

// Belirli panel + exit index için bu index'i kapsayan bundle'ı bul.
// Phase 2 (swap engelleme) ve Phase 3 (bundle drag) için.
export function findBundleByPanelExitIndex(scene, panelID, exitIndex) {
  if (!scene || !panelID || exitIndex == null) return null;
  let found = null;
  scene.traverse(obj => {
    if (found) return;
    if (obj.userData?.type !== 'wireBundle') return;
    const r = obj.userData?.panelExitRange;
    if (!r || r.panelID !== panelID) return;
    if (exitIndex >= r.min && exitIndex <= r.max) found = obj;
  });
  return found;
}

// Scene'i tara: iki tıklama noktasının her ikisine de tolerans dahilinde
// yakın geçen wire'ları topla. Tolerans iki tıklamanın mesafesine göre
// dinamik (min 30, max 200 dünya birimi) — ölçek farklılıklarına dayanıklı.
function collectCandidateWiresByTwoPoints(scene, pA, pB, tol) {
  const span = Math.hypot(pB.x - pA.x, pB.y - pA.y);
  const tolerance = (typeof tol === 'number') ? tol : Math.max(30, Math.min(200, span * 0.15));
  const out = [];
  let scanned = 0, bundleable = 0, rejected = 0;
  let bestRejectedDist = Infinity;
  scene.traverse(obj => {
    if (!obj.isLine) return;
    scanned++;
    if (!isBundleableWire(obj)) return;
    bundleable++;
    const pts = getWorldPts(obj);
    if (pts.length < 2) return;
    const projA = projectOnPolyline(pts, pA);
    const projB = projectOnPolyline(pts, pB);
    if (!projA || !projB) return;
    const maxD = Math.max(projA.dist, projB.dist);
    if (maxD > tolerance) {
      rejected++;
      if (maxD < bestRejectedDist) bestRejectedDist = maxD;
      return;
    }
    const crossing = findWireCrossingFromTwoPoints(obj, pA, pB);
    if (!crossing) return;
    out.push({ wire: obj, crossing });
  });
  return out;
}

// Scene'i tara: her iki slice'ı da geçen wire'ları topla.
function collectCandidateWiresBySlices(scene, mergeA, mergeB, openA, openB) {
  const all = [];
  let scanned = 0, bundleable = 0;
  scene.traverse(obj => {
    if (!obj.isLine) return;
    scanned++;
    if (!isBundleableWire(obj)) return;
    bundleable++;
    const crossing = findWireCrossingFromSlices(obj, mergeA, mergeB, openA, openB);
    if (!crossing) return;
    all.push({ wire: obj, crossing });
  });

  // Rep line aday olarak geldiyse, o bundle'ın iç wire'larını (orijinal + extraLine)
  // adaylardan çıkar. Aksi halde dış bundle hem rep line hem alttaki wire parçalarını
  // alır → karışık aday seti, paralel grup bozulur.
  const repCands = all.filter(c => c.wire.userData?.isBundleRep);
  let out = all;
  if (repCands.length > 0) {
    const innerIds = new Set();
    for (const r of repCands) {
      let p = r.wire.parent;
      while (p && p.userData?.type !== 'wireBundle') p = p.parent;
      if (!p) continue;
      for (const wid of (p.userData.wireIds || [])) {
        if (wid != null) innerIds.add(String(wid));
      }
    }
    out = all.filter(c => {
      if (c.wire.userData?.isBundleRep) return true;
      const ud = c.wire.userData || {};
      const candId = String(ud.id ?? ud.wireId ?? '');
      if (innerIds.has(candId)) return false;
      // extraLine id formatı: `${baseId}-bundleExtra`
      const baseFor = ud._bundleExtraFor != null ? String(ud._bundleExtraFor) : null;
      if (baseFor && innerIds.has(baseFor)) return false;
      return true;
    });
  }

  return out;
}

// Bir polyline'ı N eşit-arc adımıyla örnekle (köşeleri korur).
function samplePolyline(pts, N) {
  let totalLen = 0;
  const segLens = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const len = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    segLens.push(len);
    totalLen += len;
  }
  if (totalLen < 1e-6 || pts.length < 2) {
    return Array.from({ length: N }, () => ({ x: pts[0]?.x || 0, y: pts[0]?.y || 0 }));
  }
  const samples = [];
  const step = totalLen / (N - 1);
  let segIdx = 0;
  let segCumArc = 0;
  for (let i = 0; i < N; i++) {
    const targetArc = i * step;
    while (segIdx < pts.length - 1 && segCumArc + segLens[segIdx] < targetArc) {
      segCumArc += segLens[segIdx];
      segIdx++;
    }
    if (segIdx >= pts.length - 1) {
      const last = pts[pts.length - 1];
      samples.push({ x: last.x, y: last.y });
      continue;
    }
    const localArc = targetArc - segCumArc;
    const t = segLens[segIdx] > 1e-9 ? localArc / segLens[segIdx] : 0;
    const a = pts[segIdx], b = pts[segIdx + 1];
    samples.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  }
  return samples;
}

// guidePath üzerinde verilen sample index'lerinin arc-length konumlarını
// hesaplar — slide-drag'da slice'ın corner-bridge snap mesafesini ölçmek için.
function computeCornerArcs(guide, cornerIndices) {
  if (!guide || guide.length < 2 || !cornerIndices?.length) return [];
  // Kümülatif arc tablosu
  const cum = [0];
  for (let i = 0; i < guide.length - 1; i++) {
    cum.push(cum[i] + Math.hypot(guide[i + 1].x - guide[i].x, guide[i + 1].y - guide[i].y));
  }
  return cornerIndices.map(ci => cum[ci] || 0);
}

// Inner-curve corner verisini hesaplar: her "konsept köşe" için iç bükeyde
// kalan (en kısa arc'lı) wire'ın vertex'ini, oradan önceki/sonraki segment
// yönüyle birlikte döner. Slide-drag bu noktaya euclidean mesafeyle snap
// yapar (kullanıcı formülü: X = (wireCount-1)/2 * 5 + 5).
function computeInnerCornerData(enriched) {
  if (!enriched?.length) return [];
  const wireVertexInfo = [];
  for (let wireIdx = 0; wireIdx < enriched.length; wireIdx++) {
    const pts = enriched[wireIdx].crossing.pts;
    if (pts.length < 4) continue;
    let total = 0;
    const segLens = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const L = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
      segLens.push(L);
      total += L;
    }
    if (total < 1) continue;
    let acc = 0;
    for (let v = 1; v < pts.length - 1; v++) {
      acc += segLens[v - 1];
      const fraction = acc / total;
      const bp = pts[v - 1];
      const np = pts[v + 1];
      const bdL = Math.hypot(bp.x - pts[v].x, bp.y - pts[v].y);
      const pdL = Math.hypot(np.x - pts[v].x, np.y - pts[v].y);
      wireVertexInfo.push({
        wireIdx, fraction,
        point: { x: pts[v].x, y: pts[v].y },
        beforeDir: bdL > 1e-6 ? { x: (bp.x - pts[v].x) / bdL, y: (bp.y - pts[v].y) / bdL } : { x: 0, y: 0 },
        pastDir:   pdL > 1e-6 ? { x: (np.x - pts[v].x) / pdL, y: (np.y - pts[v].y) / pdL } : { x: 0, y: 0 },
      });
    }
  }
  if (!wireVertexInfo.length) return [];
  wireVertexInfo.sort((a, b) => a.fraction - b.fraction);
  // Cluster (≤5% fraction farkı)
  const clusters = [];
  for (const info of wireVertexInfo) {
    if (!clusters.length || info.fraction - clusters[clusters.length - 1].maxFraction > 0.05) {
      clusters.push({ items: [info], maxFraction: info.fraction });
    } else {
      clusters[clusters.length - 1].items.push(info);
      clusters[clusters.length - 1].maxFraction = info.fraction;
    }
  }
  // İç bükey (inner-curve) köşe = en küçük fraction'a sahip wire (en kısa arc → iç eğri)
  return clusters.map(c => {
    const inner = c.items[0];
    return { point: inner.point, beforeDir: inner.beforeDir, pastDir: inner.pastDir };
  });
}

// Wire vertex'leri üzerinden bridge zone'larını hesaplar — guide bend'ine
// bakmadan, wire vertex fraction'ını GUIDE SAMPLE INDEX'ine map ederek doğru
// guide arc-length konumunu bulur (önceki sürüm fraction × totalGuideArc ile
// yanlış mapping yapıyordu çünkü wire ve guide arc farklı).
function computeCornerBridgeZones(guide, _cornerIndices, enriched) {
  if (!guide || guide.length < 2 || !enriched?.length) return [];
  // Cumulative guide arc per sample
  const cumGuideArc = [0];
  for (let i = 0; i < guide.length - 1; i++) {
    cumGuideArc.push(cumGuideArc[i] + Math.hypot(guide[i + 1].x - guide[i].x, guide[i + 1].y - guide[i].y));
  }
  const lastIdx = guide.length - 1;

  const guideArcsPerVertex = [];
  for (const e of enriched) {
    const pts = e.crossing.pts;
    if (pts.length < 4) continue;
    let total = 0;
    const segLens = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const L = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
      segLens.push(L);
      total += L;
    }
    if (total < 1) continue;
    let acc = 0;
    for (let i = 0; i < segLens.length - 1; i++) {
      acc += segLens[i];
      const fraction = acc / total;
      // Wire fraction → guide sample index (same fraction along sample array)
      const sampleIdx = Math.max(0, Math.min(lastIdx, Math.round(fraction * lastIdx)));
      guideArcsPerVertex.push(cumGuideArc[sampleIdx]);
    }
  }
  if (!guideArcsPerVertex.length) return [];

  guideArcsPerVertex.sort((a, b) => a - b);
  // Cluster: yakın guide arc'lar (≤30 birim) tek köşe
  const CLUSTER_GAP = 30;
  const clusters = [];
  for (const a of guideArcsPerVertex) {
    if (!clusters.length || a - clusters[clusters.length - 1].max > CLUSTER_GAP) {
      clusters.push({ min: a, max: a });
    } else {
      clusters[clusters.length - 1].max = a;
    }
  }
  return clusters.map(c => ({
    cornerArc: (c.min + c.max) / 2,
    min: c.min,
    max: c.max,
  }));
}

// Bir polyline'da yön değişimi büyük olan vertex index'lerini bulur (köşeler).
// Yakın olan köşeler tek köşe olarak birleştirilir (mergeWindow).
// Slide-drag sırasında snap target olarak kullanılır — slice köşelere yakınken
// wire'ların transition zone'una girmesin diye.
function computeGuideCorners(guidePath, angleThresholdDeg = 15, mergeWindow = 4) {
  if (!guidePath || guidePath.length < 3) return [];
  const raw = [];
  for (let i = 1; i < guidePath.length - 1; i++) {
    const v0 = guidePath[i - 1], v1 = guidePath[i], v2 = guidePath[i + 1];
    const dx1 = v1.x - v0.x, dy1 = v1.y - v0.y;
    const dx2 = v2.x - v1.x, dy2 = v2.y - v1.y;
    const len1 = Math.hypot(dx1, dy1);
    const len2 = Math.hypot(dx2, dy2);
    if (len1 < 1e-6 || len2 < 1e-6) continue;
    const cos = (dx1 * dx2 + dy1 * dy2) / (len1 * len2);
    const ang = Math.acos(Math.max(-1, Math.min(1, cos))) * 180 / Math.PI;
    if (ang > angleThresholdDeg) raw.push(i);
  }
  // Yakın köşeleri (ortalama ile) tek köşe say
  const merged = [];
  for (const c of raw) {
    if (merged.length === 0 || c - merged[merged.length - 1] > mergeWindow) {
      merged.push(c);
    } else {
      merged[merged.length - 1] = Math.round((merged[merged.length - 1] + c) / 2);
    }
  }
  return merged;
}

// Bundle'a dahil tüm wire'ların TAM polyline'larını (sadece slice'lar arası
// değil) örnekleyip ortalamasını alarak "guidePath" üretir. Slice-slide drag
// bu sabit güzergâh üzerine projekte edilir; rebuild'lerle değişmez.
function buildGuidePath(enriched, samples = 200) {
  if (!enriched.length) return [];
  // İlk wire'ı referans alıp diğerlerini ona göre yönlendir (paralel-ters
  // wire'larda örnekleme uyumlu olsun).
  const ref = samplePolyline(enriched[0].crossing.pts, samples);
  const aligned = [ref];
  for (let i = 1; i < enriched.length; i++) {
    const s = samplePolyline(enriched[i].crossing.pts, samples);
    const dForward = Math.hypot(s[0].x - ref[0].x, s[0].y - ref[0].y);
    const dReverse = Math.hypot(s[samples - 1].x - ref[0].x, s[samples - 1].y - ref[0].y);
    aligned.push(dReverse < dForward ? s.slice().reverse() : s);
  }
  const guide = [];
  for (let i = 0; i < samples; i++) {
    let sx = 0, sy = 0;
    for (const ws of aligned) { sx += ws[i].x; sy += ws[i].y; }
    guide.push({ x: sx / aligned.length, y: sy / aligned.length });
  }
  return guide;
}

// Wire stub uçlarını ilk toplayıcı çizgisi (entry/exit collector'ın anchor'ı =
// bundlePath[0]/[N-1]) üzerine hizalar. Her wire kendi backStubEnd_i'de
// (slice + stubLen) bitmek yerine, ortalama p0/pN konumundaki perpendicular
// hatla kesişen noktada biter → tüm wire'lar görsel olarak ilk toplayıcıda
// toplanır. Bundle midline aligned uçlarla yeniden hesaplanır.
// Per-wire stub clampleme: stub'ın bundle içinde KÖŞE geçmemesi için
// her wire için kendi enter/exit segmentinin uzunluğunu üst sınır yapar.
function clampStubsPerWire(enriched, requestedBack, requestedForw) {
  const backs = []; const forws = [];
  for (const e of enriched) {
    const { pts, enterIdx, exitIdx, enterPoint, exitPoint } = e.crossing;
    let backLimit, forwLimit;
    if (enterIdx === exitIdx) {
      // Tek segment — back ve forw aynı segmentin bölgelerini paylaşır
      const span = Math.hypot(exitPoint.x - enterPoint.x, exitPoint.y - enterPoint.y);
      backLimit = Math.max(0, span / 2 - 1);
      forwLimit = backLimit;
    } else {
      // Çoklu segment: back enterPoint→pts[enterIdx+1] uzunluğu kadar
      backLimit = Math.hypot(pts[enterIdx + 1].x - enterPoint.x, pts[enterIdx + 1].y - enterPoint.y) - 1;
      forwLimit = Math.hypot(exitPoint.x - pts[exitIdx].x, exitPoint.y - pts[exitIdx].y) - 1;
    }
    backs.push(Math.max(0, Math.min(requestedBack, backLimit)));
    forws.push(Math.max(0, Math.min(requestedForw, forwLimit)));
  }
  return { backs, forws };
}

function alignStubsToCollectorLine(enriched, backStubEnds, forwStubEnds, bundlePath) {
  if (bundlePath.length < 2) {
    return { backStubEnds, forwStubEnds, alignedBackStubs: null, alignedForwStubs: null };
  }
  const p0 = bundlePath[0];
  const pN = bundlePath[bundlePath.length - 1];

  // Wire'ın in-bundle entry/exit segmentine projeksiyon. projectOnPolyline tüm
  // polyline'ı tarayıp euclidean closest döndürür → köşe-geçen wire'larda p0/pN
  // YANLIŞ segmente (köşenin diğer tarafına) düşebilir. Burada wire'ın bundle
  // içindeki entry parçasına (enterPoint→pts[enterIdx+1] veya exitPoint) ya da
  // exit parçasına (pts[exitIdx]→exitPoint veya enterPoint) tek-segment projeksiyon
  // yapılır → projeksiyon her zaman doğru segmentte kalır, köşeyi geçmez.
  const projectOnSeg = (a, b, p) => {
    const dx = b.x - a.x, dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-9) return { x: a.x, y: a.y };
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return { x: a.x + dx * t, y: a.y + dy * t };
  };
  const projectEntry = (cr, p) => {
    const b = (cr.enterIdx === cr.exitIdx) ? cr.exitPoint : cr.pts[cr.enterIdx + 1];
    return projectOnSeg(cr.enterPoint, b, p);
  };
  const projectExit = (cr, p) => {
    const a = (cr.enterIdx === cr.exitIdx) ? cr.enterPoint : cr.pts[cr.exitIdx];
    return projectOnSeg(a, cr.exitPoint, p);
  };

  const newBack = enriched.map((e, i) => projectEntry(e.crossing, p0) || backStubEnds[i]);
  const newForw = enriched.map((e, i) => projectExit(e.crossing, pN) || forwStubEnds[i]);
  // Per-wire stub uzunlukları — enterPoint→aligned end (tek segment varsayımı:
  // slice ile p0 arası genelde wire'ın aynı segmentinde). Köşe içerirse hafif
  // sapma olur ama görsel hizalama için yeterli.
  const alignedBackStubs = enriched.map((e, i) =>
    Math.hypot(newBack[i].x - e.crossing.enterPoint.x, newBack[i].y - e.crossing.enterPoint.y)
  );
  const alignedForwStubs = enriched.map((e, i) =>
    Math.hypot(newForw[i].x - e.crossing.exitPoint.x, newForw[i].y - e.crossing.exitPoint.y)
  );
  return {
    backStubEnds: newBack,
    forwStubEnds: newForw,
    alignedBackStubs,
    alignedForwStubs,
  };
}

// Stub uzunluğunu wire'ların enter→exit aralığının yarısına sınırlar.
// İki-nokta akışında küçük bundle'larda iki stub'ın birbirini aşmasını engeller.
function clampStubLen(enriched, requested) {
  let limit = Infinity;
  for (const e of enriched) {
    const span = dist(e.crossing.enterPoint, e.crossing.exitPoint);
    const maxForThis = Math.max(0, span / 2 - 1);
    if (maxForThis < limit) limit = maxForThis;
  }
  return Math.max(0, Math.min(requested, limit));
}

// Scene'i tara ve rect ile kesişen wire'ları topla
function collectCandidateWires(scene, rect) {
  const out = [];
  scene.traverse(obj => {
    if (!obj.isLine) return;
    if (!isBundleableWire(obj)) return;
    const crossing = findWireRectCrossing(obj, rect);
    if (!crossing) return;
    out.push({ wire: obj, crossing });
  });
  return out;
}

// Entry direction'a göre paralel grup seç
function selectParallelGroup(candidates) {
  const bins = [];
  for (const c of candidates) {
    let bin = bins.find(b => dirsMatch(b.dir, c.crossing.entryDir, ANGLE_TOL_DEG * 1.5));
    if (!bin) { bin = { dir: c.crossing.entryDir, items: [] }; bins.push(bin); }
    bin.items.push(c);
  }
  bins.sort((a, b) => b.items.length - a.items.length);
  return bins[0] || { items: [], dir: { angle: 0 } };
}

// ─── Wire truncation ─────────────────────────────────────────────────────────

// Wire'ı bundle extent'ine göre kes. bwdSteps: backward segment sayısı,
// fwdSteps: forward segment sayısı. Bundle bu vertex'ler arasındaki her şeyi
// yutar; geriye sadece wire'ın dışı + 20 birim stub kalır.
function truncateWireWithSteps(wire, crossing, bwdSteps, fwdSteps, backStubLen = STUB_LEN, forwStubLen = STUB_LEN) {
  const { pts, enterIdx, exitIdx, enterPoint, exitPoint } = crossing;
  const backSegs = getBackwardSegs(crossing);
  const forwSegs = getForwardSegs(crossing);
  const bStub = backStubLen;
  const fStub = forwStubLen;

  // Backward side — keep vertices from 0 to backward extreme, then add 20-unit stub
  // that extends INTO bundle zone (toward rect)
  let backSidePts = [];
  let backStubEnd = null; // world point — bundle'ın gerçek backward başlangıcı (stub sonu)
  if (bwdSteps === 0) {
    // Wire'ın backward extreme'i enterPoint. Görünür stub enterPoint'ten
    // forward'a doğru (rect içi + forwSegs) 20 birim uzanır.
    for (let i = 0; i <= enterIdx; i++) backSidePts.push(pts[i]);
    // enterPoint → exitPoint → forwSegs boyunca 20 birim yürü
    const stubPath = walkForwardFromPoint(crossing, bStub);
    for (let i = 0; i < stubPath.length; i++) backSidePts.push(stubPath[i]);
    backStubEnd = stubPath[stubPath.length - 1];
  } else {
    const limitVertex = backSegs[bwdSteps - 1].endVertexIdx;
    for (let i = 0; i <= limitVertex; i++) backSidePts.push(pts[i]);
    // Stub: V(limitVertex)'ten forward direction'a (backSegs reversed) bStub birim
    let remaining = bStub;
    for (let i = bwdSteps - 1; i >= 0 && remaining > 0; i--) {
      const seg = backSegs[i];
      const segLen = dist(seg.start, seg.end);
      if (segLen < 1e-6) continue;
      if (segLen >= remaining) {
        const frac = remaining / segLen;
        const p = {
          x: seg.end.x + (seg.start.x - seg.end.x) * frac,
          y: seg.end.y + (seg.start.y - seg.end.y) * frac,
        };
        backSidePts.push(p);
        backStubEnd = p;
        remaining = 0;
      } else {
        backSidePts.push({ x: seg.start.x, y: seg.start.y });
        remaining -= segLen;
        backStubEnd = { x: seg.start.x, y: seg.start.y };
      }
    }
    // Stub enterPoint'i geçtiyse ve hâlâ remaining varsa rect içi + forward'a uzat
    if (remaining > 0) {
      const extra = walkForwardFromPoint(crossing, remaining);
      for (let i = 0; i < extra.length; i++) backSidePts.push(extra[i]);
      backStubEnd = extra[extra.length - 1] || backStubEnd;
    }
  }

  // Forward side — stub from (forward extreme - 20 units inward) to end of wire
  let forwSidePts = [];
  let forwStubStart = null;
  if (fwdSteps === 0) {
    const stubPath = walkBackwardFromPoint(crossing, fStub);
    for (let i = 0; i < stubPath.length; i++) forwSidePts.push(stubPath[i]);
    forwStubStart = stubPath[0];
    for (let i = exitIdx + 1; i < pts.length; i++) forwSidePts.push(pts[i]);
  } else {
    // Stub path from fStub units backward of forward extreme → to forward extreme
    let remaining = fStub;
    const stubPath = [];
    for (let i = fwdSteps - 1; i >= 0 && remaining > 0; i--) {
      const seg = forwSegs[i];
      const segLen = dist(seg.start, seg.end);
      if (segLen < 1e-6) continue;
      if (segLen >= remaining) {
        const frac = remaining / segLen;
        const p = {
          x: seg.end.x + (seg.start.x - seg.end.x) * frac,
          y: seg.end.y + (seg.start.y - seg.end.y) * frac,
        };
        stubPath.unshift(p);
        remaining = 0;
      } else {
        stubPath.unshift({ x: seg.start.x, y: seg.start.y });
        remaining -= segLen;
      }
    }
    if (remaining > 0) {
      const extra = walkBackwardFromPoint(crossing, remaining);
      for (let i = 0; i < extra.length; i++) stubPath.unshift(extra[i]);
    }
    forwStubStart = stubPath[0] || null;
    // forwSidePts: stubPath + [limitVertex ... n-1]
    for (const p of stubPath) forwSidePts.push(p);
    const limitVertex = forwSegs[fwdSteps - 1].endVertexIdx;
    for (let i = limitVertex; i < pts.length; i++) forwSidePts.push(pts[i]);
  }

  // Backward side tek vertex ise (tüm wire bundle'landı) o tarafı boşalt
  if (backSidePts.length < 2) backSidePts = [];
  if (forwSidePts.length < 2) forwSidePts = [];

  // Wire'ın iki ayrık parçasını tek geometry'de birleştiremeyiz (ortasında
  // boşluk var). Bu yüzden: backward side'ı mevcut wire'a yaz; forward side
  // için yeni bir Line oluştur (wire'ın kopyası olarak). Expand'da ikincisi
  // silinir ve orijinal wire geri yüklenir.
  if (backSidePts.length === 0 && forwSidePts.length === 0) {
    wire.visible = false;
    return null;
  }

  wire.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(wire.matrixWorld).invert();

  const mainPts = backSidePts.length > 0 ? backSidePts : forwSidePts;
  const extraPts = backSidePts.length > 0 && forwSidePts.length > 0 ? forwSidePts : [];

  const writePts = (line, worldPts) => {
    const arr = new Float32Array(worldPts.length * 3);
    const v = new THREE.Vector3();
    for (let i = 0; i < worldPts.length; i++) {
      v.set(worldPts[i].x, worldPts[i].y, 0).applyMatrix4(inv);
      arr[i * 3] = v.x; arr[i * 3 + 1] = v.y; arr[i * 3 + 2] = v.z;
    }
    line.geometry.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    line.geometry.attributes.position.needsUpdate = true;
    line.geometry.computeBoundingBox();
    line.geometry.computeBoundingSphere();
    if (typeof line.computeLineDistances === 'function') line.computeLineDistances();
  };

  writePts(wire, mainPts);
  wire.visible = true;

  let extraLine = null;
  if (extraPts.length > 0) {
    const extraGeom = new THREE.BufferGeometry();
    const extraMat = wire.material ? wire.material.clone() : new THREE.LineBasicMaterial({ color: 0xff0000 });
    extraLine = new THREE.Line(extraGeom, extraMat);
    extraLine.position.copy(wire.position);
    extraLine.rotation.copy(wire.rotation);
    extraLine.scale.copy(wire.scale);
    extraLine.userData = {
      ...wire.userData,
      _bundleExtraFor: wire.userData?.id || wire.uuid,
      _bundleTransient: true,
      id: `${wire.userData?.id || wire.uuid}-bundleExtra`,
    };
    wire.parent?.add(extraLine);
    writePts(extraLine, extraPts);
  }
  return extraLine;
}

// ─── Collector + Representative ──────────────────────────────────────────────

function createCollectorGroup(dir, perp, anchor, halfSpan, inwardSign, color = BUNDLE_COLOR_COLLECTOR) {
  const group = new THREE.Group();
  const mat = new THREE.LineBasicMaterial({ color });
  for (let k = 0; k < COLLECTOR_COUNT; k++) {
    const off = k * COLLECTOR_SPACING * inwardSign;
    const cx = anchor.x + dir.x * off;
    const cy = anchor.y + dir.y * off;
    const p1 = new THREE.Vector3(cx - perp.x * halfSpan, cy - perp.y * halfSpan, 0);
    const p2 = new THREE.Vector3(cx + perp.x * halfSpan, cy + perp.y * halfSpan, 0);
    const g = new THREE.BufferGeometry().setFromPoints([p1, p2]);
    const line = new THREE.Line(g, mat);
    line.userData = { isBundlePart: true, role: 'collector-line', bundleIndex: k };
    group.add(line);
  }
  return group;
}

// Bir polyline'ı her iki ucundan arc-length boyunca trimStart / trimEnd birim
// kısaltır. Polyline-aware: ara köşelerden geçen path'lerde de doğru çalışır.
function trimPathFromEnds(path, trimStart, trimEnd) {
  if (!path || path.length < 2) return path ? path.slice() : [];
  let totalLen = 0;
  for (let i = 0; i < path.length - 1; i++) totalLen += dist(path[i], path[i + 1]);
  if (trimStart + trimEnd >= totalLen - 1e-6) {
    return []; // bundle çok kısa → rep line skip
  }
  const result = path.map(p => ({ x: p.x, y: p.y }));
  // Baştan trim
  let remaining = trimStart;
  while (remaining > 1e-9 && result.length >= 2) {
    const segLen = dist(result[0], result[1]);
    if (segLen <= remaining + 1e-9) {
      result.shift();
      remaining -= segLen;
    } else {
      const t = remaining / segLen;
      result[0] = {
        x: result[0].x + (result[1].x - result[0].x) * t,
        y: result[0].y + (result[1].y - result[0].y) * t,
      };
      remaining = 0;
    }
  }
  // Sondan trim
  remaining = trimEnd;
  while (remaining > 1e-9 && result.length >= 2) {
    const li = result.length - 1;
    const segLen = dist(result[li - 1], result[li]);
    if (segLen <= remaining + 1e-9) {
      result.pop();
      remaining -= segLen;
    } else {
      const t = remaining / segLen;
      result[li] = {
        x: result[li].x + (result[li - 1].x - result[li].x) * t,
        y: result[li].y + (result[li - 1].y - result[li].y) * t,
      };
      remaining = 0;
    }
  }
  return result;
}

function createRepresentativePolyline(pointsWorld, color = BUNDLE_COLOR_REPRESENT, bundleId = null) {
  // depthTest=false + renderOrder ile rep line her zaman collector çizgilerinin
  // ÜZERİNDE çizilir → wire uçlarından çıktığı (ilk toplayıcı çizgisi konumu)
  // net görünür, collector cluster içinde gizlenmez.
  const mat = new THREE.LineBasicMaterial({ color, depthTest: false, depthWrite: false });
  const g = new THREE.BufferGeometry().setFromPoints(
    pointsWorld.map(p => new THREE.Vector3(p.x, p.y, 0.05))
  );
  const line = new THREE.Line(g, mat);
  line.renderOrder = 10;
  // Wire-benzeri userData: type/isWire ile diğer wire pipeline (selection, snap,
  // gizmo) onu wire olarak tanır. isBundleRep ile isBundleableWire'da bypass'lanır.
  // isBundlePart hâlâ true → cleanup ve filter logic'leri korunur (sadece
  // bundleableWire isBundleRep'i öncelikli kabul eder).
  line.userData = {
    isBundlePart: true,
    role: 'representative-line',
    isBundleRep: true,
    type: 'wire',
    isWire: true,
    bundleId,
    wireId: bundleId, // wire pipeline'ı id ile takip ediyor
    id: bundleId,
  };
  return line;
}

// Wire'ların segment endpoint'lerinin ortalamasını alarak midline oluştur
function buildMidlinePoints(wireSegsArray, steps, startPointsWorld) {
  const points = [];
  // Başlangıç: her wire'ın segment 0 start'ı — ortalaması
  const avgStart = averagePoints(startPointsWorld);
  points.push(avgStart);
  // Her step için ortalama end noktası
  for (let step = 0; step < steps; step++) {
    const ends = wireSegsArray.map(segs => segs[step].end);
    points.push(averagePoints(ends));
  }
  return points;
}

function averagePoints(pts) {
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  return { x: x / pts.length, y: y / pts.length };
}

// ─── Stub end + midline helper'ları ───

function computeBackwardStubEnd(crossing, bwdSteps, stubLen) {
  const backSegs = getBackwardSegs(crossing);
  if (bwdSteps === 0) {
    const stubPath = walkForwardFromPoint(crossing, stubLen);
    return stubPath[stubPath.length - 1] || { x: crossing.enterPoint.x, y: crossing.enterPoint.y };
  }
  let remaining = stubLen;
  for (let i = bwdSteps - 1; i >= 0; i--) {
    const seg = backSegs[i];
    const segLen = dist(seg.start, seg.end);
    if (segLen < 1e-6) continue;
    if (segLen >= remaining) {
      const frac = remaining / segLen;
      return {
        x: seg.end.x + (seg.start.x - seg.end.x) * frac,
        y: seg.end.y + (seg.start.y - seg.end.y) * frac,
      };
    }
    remaining -= segLen;
  }
  const extra = walkForwardFromPoint(crossing, remaining);
  return extra[extra.length - 1] || { x: crossing.enterPoint.x, y: crossing.enterPoint.y };
}

function computeForwardStubEnd(crossing, fwdSteps, stubLen) {
  const forwSegs = getForwardSegs(crossing);
  if (fwdSteps === 0) {
    const stubPath = walkBackwardFromPoint(crossing, stubLen);
    return stubPath[0] || { x: crossing.exitPoint.x, y: crossing.exitPoint.y };
  }
  let remaining = stubLen;
  for (let i = fwdSteps - 1; i >= 0; i--) {
    const seg = forwSegs[i];
    const segLen = dist(seg.start, seg.end);
    if (segLen < 1e-6) continue;
    if (segLen >= remaining) {
      const frac = remaining / segLen;
      return {
        x: seg.end.x + (seg.start.x - seg.end.x) * frac,
        y: seg.end.y + (seg.start.y - seg.end.y) * frac,
      };
    }
    remaining -= segLen;
  }
  const extra = walkBackwardFromPoint(crossing, remaining);
  return extra[0] || { x: crossing.exitPoint.x, y: crossing.exitPoint.y };
}

// Bir wire'ın polyline'ında iki nokta arasındaki yolu çıkarır (inclusive).
// Points world koordinatlarında.
function extractWireSubpath(pts, startPoint, endPoint) {
  const findSeg = (p) => {
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const dx = b.x - a.x, dy = b.y - a.y;
      const lenSq = dx * dx + dy * dy;
      if (lenSq < 1e-9) continue;
      const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
      if (t >= -0.02 && t <= 1.02) {
        const px = a.x + dx * t;
        const py = a.y + dy * t;
        if (Math.hypot(p.x - px, p.y - py) < 2) {
          return { idx: i, t: Math.max(0, Math.min(1, t)) };
        }
      }
    }
    return null;
  };

  const s = findSeg(startPoint);
  const e = findSeg(endPoint);
  if (!s || !e) return [startPoint, endPoint];

  const result = [{ x: startPoint.x, y: startPoint.y }];
  if (s.idx === e.idx) {
    result.push({ x: endPoint.x, y: endPoint.y });
    return result;
  }
  const [lo, hi] = s.idx < e.idx ? [s, e] : [e, s];
  // Traversal direction: from startPoint toward endPoint
  if (s.idx < e.idx) {
    for (let i = s.idx + 1; i <= e.idx; i++) result.push(pts[i]);
    result.push({ x: endPoint.x, y: endPoint.y });
  } else {
    for (let i = s.idx; i > e.idx; i--) result.push(pts[i]);
    result.push({ x: endPoint.x, y: endPoint.y });
  }
  return result;
}

// Bundle midline path: her wire'ın backStubEnd'den forwStubEnd'e kadar olan
// sub-yolunu çıkar, wire'lar arası aynı index'teki noktaları ortalar.
function buildBundleMidlinePath(enriched, backStubEnds, forwStubEnds) {
  const perWirePaths = enriched.map((e, idx) => {
    const raw = extractWireSubpath(e.crossing.pts, backStubEnds[idx], forwStubEnds[idx]);
    // Yakın duplicate'leri temizle
    const clean = [raw[0]];
    for (let i = 1; i < raw.length; i++) {
      if (dist(clean[clean.length - 1], raw[i]) > 1e-3) clean.push(raw[i]);
    }
    return clean;
  });
  const minLen = Math.min(...perWirePaths.map(p => p.length));
  const mid = [];
  for (let i = 0; i < minLen; i++) {
    const pts = perWirePaths.map(p => p[i]);
    mid.push(averagePoints(pts));
  }
  return mid;
}

// Marker'ın gerçek dünya merkezi. Slide-drag her frame'de N_marker kez
// çağrıldığı için Box3.setFromObject (alt-obje gezme + matrix uygulama)
// pahalı kaçıyordu. Bunun yerine: önce userData.midPoint, sonra ilk Line
// child'ın geometri vertex ortalaması — tek allocation, lineer maliyet.
const _markerVec = new THREE.Vector3();
function getMarkerCenter(obj) {
  // 1) Hızlı yol: lighting marker'larında saklı orta nokta.
  const mp = obj.userData?.midPoint;
  if (mp && typeof mp.x === 'number' && typeof mp.y === 'number') {
    return { x: mp.x, y: mp.y };
  }
  // 2) Doğrudan Line ise: vertex avg'ı LOCAL'da → matrixWorld ile dönüştür.
  const fromLine = (line) => {
    const pos = line.geometry?.attributes?.position;
    if (!pos || pos.count === 0) return null;
    let sx = 0, sy = 0;
    for (let i = 0; i < pos.count; i++) { sx += pos.getX(i); sy += pos.getY(i); }
    sx /= pos.count; sy /= pos.count;
    line.updateMatrixWorld(true);
    _markerVec.set(sx, sy, 0).applyMatrix4(line.matrixWorld);
    return { x: _markerVec.x, y: _markerVec.y };
  };
  if (obj.isLine) {
    const c = fromLine(obj);
    if (c) return c;
  }
  // 3) Group'sa: ilk Line child'ı bul (marker tipik olarak 1-2 child Line).
  if (obj.children?.length) {
    for (const child of obj.children) {
      if (child.isLine) {
        const c = fromLine(child);
        if (c) return c;
      }
    }
  }
  // 4) Fallback — getWorldPosition (genelde origin döner ama emniyet için).
  obj.getWorldPosition(_markerVec);
  return { x: _markerVec.x, y: _markerVec.y };
}

function hideWireMarkersInBundle(scene, enriched, bwdSteps, fwdSteps) {
  const hidden = [];
  const zonesPerWire = enriched.map(e => buildBundleZonePath(e.crossing, bwdSteps, fwdSteps));
  // Bundle'da yer alan wire ID'lerini topla — parentWireId filtresi için.
  const bundleWireIds = new Set(
    enriched.map(e => e.wire.userData?.id).filter(Boolean)
  );

  scene.traverse(obj => {
    const ud = obj.userData || {};
    if (ud.role !== 'wireMarker' && ud.type !== 'wireMarker') return;
    if (!obj.visible) return; // zaten gizli
    // Marker'ın parent wire'ı bundle'a aitse ya da bilgi yoksa devam.
    const parentId = ud.parentWireId || ud.wireId || ud.wireID;
    if (parentId && !bundleWireIds.has(parentId)) return;
    const center = getMarkerCenter(obj);
    // Bundle wire'larından herhangi birinin zone'una yakın mı?
    for (const zone of zonesPerWire) {
      if (pointNearPolyline(center, zone, 5)) {
        obj.visible = false;
        hidden.push({ uuid: obj.uuid, origVisible: true });
        return;
      }
    }
  });
  return hidden;
}

// Bir wire'ın bundle zone'undaki path'i çıkarır (world points).
// Slice-flow'da enterIdx ≠ exitIdx olabildiği için enterPoint→exitPoint arası
// düz çizgi değil, wire'ın orijinal köşelerinden geçer.
function buildBundleZonePath(crossing, bwdSteps, fwdSteps) {
  const { pts, enterIdx, exitIdx, enterPoint, exitPoint } = crossing;
  const backSegs = getBackwardSegs(crossing);
  const forwSegs = getForwardSegs(crossing);

  const path = [];
  // Backward extreme
  if (bwdSteps > 0) {
    path.push({ x: backSegs[bwdSteps - 1].end.x, y: backSegs[bwdSteps - 1].end.y });
    for (let i = bwdSteps - 1; i >= 0; i--) path.push({ x: backSegs[i].start.x, y: backSegs[i].start.y });
  } else {
    path.push({ x: enterPoint.x, y: enterPoint.y });
  }
  // enterPoint → ara köşeler → exitPoint (polyline-aware)
  if (enterIdx !== exitIdx) {
    for (let i = enterIdx + 1; i <= exitIdx; i++) {
      path.push({ x: pts[i].x, y: pts[i].y });
    }
  }
  if (dist(path[path.length - 1], exitPoint) > 1e-3) {
    path.push({ x: exitPoint.x, y: exitPoint.y });
  }
  // Forward
  if (fwdSteps > 0) {
    for (let i = 0; i < fwdSteps; i++) {
      path.push({ x: forwSegs[i].end.x, y: forwSegs[i].end.y });
    }
  }
  return path;
}

// Nokta polyline'e yakın mı? (segment-wise minimum mesafe)
function pointNearPolyline(pt, polyline, tolerance = 2) {
  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i], b = polyline[i + 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-9) continue;
    const t = Math.max(0, Math.min(1, ((pt.x - a.x) * dx + (pt.y - a.y) * dy) / lenSq));
    const px = a.x + dx * t, py = a.y + dy * t;
    const d = Math.hypot(pt.x - px, pt.y - py);
    if (d <= tolerance) return true;
  }
  return false;
}

// ─── Main entry point ────────────────────────────────────────────────────────

// Bundle'ı mevcut state ile yeniden oluştur — M-drag sırasında stub length'lerini
// değiştirmek için kullanılır. Wire'ları orijinaline geri yükler, yeni
// stub length'lerle truncate eder, collector/representative'ı günceller.
export function rebuildBundleWithStubs(scene, bundleGroup, backStubLen, forwStubLen) {
  if (!bundleGroup || bundleGroup.userData?.type !== 'wireBundle') return false;

  // 1. Orijinal wire geometrilerini geri yükle
  const origs = bundleGroup.userData.originalGeometries || [];
  const wires = [];
  for (const o of origs) {
    let wire = null;
    scene.traverse(obj => {
      if (wire) return;
      if (obj.uuid === o.uuid) wire = obj;
      else if (obj.userData?.id === o.wireId && isBundleableWire(obj)) wire = obj;
    });
    if (!wire) continue;
    const arr = new Float32Array(o.positions);
    wire.geometry.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    wire.geometry.attributes.position.needsUpdate = true;
    wire.visible = true;
    wires.push(wire);
  }

  // 2. Eski extra line'ları sil
  const extraUuids = new Set(bundleGroup.userData.extraLineUuids || []);
  const toRemove = [];
  scene.traverse(obj => { if (extraUuids.has(obj.uuid)) toRemove.push(obj); });
  for (const obj of toRemove) {
    obj.parent?.remove(obj);
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material?.dispose) obj.material.dispose();
  }

  // 3. Eski marker visibility'sini geri aç
  const markerUuids = new Set(bundleGroup.userData.hiddenMarkerUuids || []);
  scene.traverse(obj => {
    if (markerUuids.has(obj.uuid)) obj.visible = true;
  });

  // 4. Bundle group içindeki eski collector/representative çocuklarını temizle
  const children = [...bundleGroup.children];
  for (const c of children) {
    bundleGroup.remove(c);
    c.traverse(obj => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material?.dispose) obj.material.dispose();
    });
  }

  // 5. Saklı bilgiyle yeniden tespit yap. Üç kaynak destekli:
  //    - userData.slices = {merge:{a,b}, open:{a,b}}  → 4-tıklama (yeni varsayılan)
  //    - userData.points = {a, b}                     → eski 2-tıklama akışı
  //    - userData.rect                                → en eski rect-drag akışı
  const savedSlices = bundleGroup.userData.slices;
  const savedPoints = bundleGroup.userData.points;
  const savedRect   = bundleGroup.userData.rect;
  let candidates = null;
  let fixedExtent = false; // bundle kullanıcı tarafından sabitlendi mi?
  if (savedSlices) {
    fixedExtent = true;
    candidates = collectCandidateWiresBySlices(
      scene, savedSlices.merge.a, savedSlices.merge.b, savedSlices.open.a, savedSlices.open.b
    );
  } else if (savedPoints) {
    fixedExtent = true;
    candidates = collectCandidateWiresByTwoPoints(scene, savedPoints.a, savedPoints.b);
  } else if (savedRect) {
    candidates = collectCandidateWires(scene, savedRect);
  } else {
    return false;
  }
  if (!candidates || candidates.length < 2) return false;

  // Slice-slide stabilizasyonu: bundle'ın orijinal wireIds set'i biliniyorsa,
  // candidates'ı bu set ile filtrele. Slice mouse hareketiyle hafifçe oynadıkça
  // sınırdaki wire'ların crossing testi pozitif/negatif arası salınıyordu →
  // wire'lar bundle'a girip çıkıyordu. Sabit set: drag boyunca wire grubu
  // değişmesin, kullanıcı kontrol kaybetmesin.
  const _originalIds = bundleGroup.userData?.wireIds;
  if (Array.isArray(_originalIds) && _originalIds.length > 0) {
    const _ids = new Set(_originalIds.map(String));
    const filtered = candidates.filter(c => {
      const wid = c.wire?.userData?.id;
      return wid != null && _ids.has(String(wid));
    });
    if (filtered.length >= 2) candidates = filtered;
  }

  const grp = selectParallelGroup(candidates);
  if (grp.items.length < 2) return false;

  const enriched = grp.items.map(it => ({
    ...it,
    forwSegs: getForwardSegs(it.crossing),
    backSegs: getBackwardSegs(it.crossing),
  }));

  let fwdSteps, bwdSteps;
  if (fixedExtent) {
    // Sabit kapsam (slice/2-nokta): bundle iki uç arasında kalır.
    fwdSteps = 0;
    bwdSteps = 0;
  } else {
    fwdSteps = lockstepWalk(enriched.map(e => e.forwSegs));
    bwdSteps = lockstepWalk(enriched.map(e => e.backSegs));
    ({ fwdSteps, bwdSteps } = limitStepsByComponents(scene, enriched, fwdSteps, bwdSteps));
  }

  const safeBack = clampStubLen(enriched, backStubLen);
  const safeForw = clampStubLen(enriched, forwStubLen);

  let backStubEnds = enriched.map(e => computeBackwardStubEnd(e.crossing, bwdSteps, safeBack));
  let forwStubEnds = enriched.map(e => computeForwardStubEnd(e.crossing, fwdSteps, safeForw));
  let bundlePath = buildBundleMidlinePath(enriched, backStubEnds, forwStubEnds);

  // Sabit kapsamlı (slice/2pt) bundle'larda wire uçlarını ilk toplayıcı
  // çizgisine hizala. Rect-flow'da (genişletilebilir) hizalama yapma.
  // Köşe-geçen bundle'larda da çalışır: alignStubsToCollectorLine projeksiyonu
  // entry/exit segmentine sınırlandırır, köşeyi geçmez.
  let alignedBackStubsArr = null, alignedForwStubsArr = null;
  if (fixedExtent) {
    const aligned = alignStubsToCollectorLine(enriched, backStubEnds, forwStubEnds, bundlePath);
    backStubEnds = aligned.backStubEnds;
    forwStubEnds = aligned.forwStubEnds;
    bundlePath = buildBundleMidlinePath(enriched, backStubEnds, forwStubEnds);
    alignedBackStubsArr = aligned.alignedBackStubs;
    alignedForwStubsArr = aligned.alignedForwStubs;
  }

  // Görsel wire sayısına göre entry stub görünürlük haritası oluştur
  const backStubVisible = new Array(enriched.length).fill(true);
  const vcRaw = bundleGroup.userData.visualWireCount;
  if (vcRaw != null && enriched.length >= 2) {
    const N = enriched.length;
    const vc = Math.max(2, Math.min(N, vcRaw));
    if (vc < N) {
      const entryDir = bundlePath.length >= 2
        ? segDir(bundlePath[0], bundlePath[1])
        : { x: 1, y: 0 };
      const entryPerp = { x: -entryDir.y, y: entryDir.x };
      const projectPerp = p => p.x * entryPerp.x + p.y * entryPerp.y;
      const sortedByPerp = enriched
        .map((_, i) => ({ i, proj: projectPerp(backStubEnds[i]) }))
        .sort((a, b) => a.proj - b.proj);
      // Ortadaki vc wireyi tut, uçtakileri gizle → entry tarafı simetrik daralır
      const hideCount = N - vc;
      const hideFromStart = Math.floor(hideCount / 2);
      const hideFromEnd = hideCount - hideFromStart;
      const selectedIdxs = new Set();
      for (let j = hideFromStart; j < N - hideFromEnd; j++) {
        selectedIdxs.add(sortedByPerp[j].i);
      }
      for (let i = 0; i < enriched.length; i++) {
        if (!selectedIdxs.has(i)) backStubVisible[i] = false;
      }
      // Seçili wirelerin gerçek perp aralığını sakla → _applyVisualScale anchor kaydırır
      const selPerps = [];
      for (let i = 0; i < enriched.length; i++) {
        if (selectedIdxs.has(i)) selPerps.push(projectPerp(backStubEnds[i]));
      }
      const selPerpMin = Math.min(...selPerps);
      const selPerpMax = Math.max(...selPerps);
      bundleGroup.userData.visualEntryPerp = {
        center: (selPerpMin + selPerpMax) / 2,
        halfSpan: (selPerpMax - selPerpMin) / 2 + COLLECTOR_PAD,
      };
    } else {
      bundleGroup.userData.visualEntryPerp = null;
    }
  }

  const newExtraLines = [];
  for (let i = 0; i < enriched.length; i++) {
    const it = enriched[i];
    const bStub = alignedBackStubsArr ? alignedBackStubsArr[i] : safeBack;
    const fStub = alignedForwStubsArr ? alignedForwStubsArr[i] : safeForw;
    const extra = truncateWireWithSteps(it.wire, it.crossing, bwdSteps, fwdSteps, bStub, fStub);
    if (extra) newExtraLines.push(extra);
    // Seçilmemiş wirelerin entry stub'ını gizle
    if (!backStubVisible[i]) it.wire.visible = false;
  }
  const hiddenMarkers = hideWireMarkersInBundle(scene, enriched, bwdSteps, fwdSteps);

  bundleGroup.userData.extraLineUuids = newExtraLines.map(x => x.uuid);
  bundleGroup.userData.hiddenMarkerUuids = hiddenMarkers.map(m => m.uuid);
  bundleGroup.userData.backStubLen = safeBack;
  bundleGroup.userData.forwStubLen = safeForw;

  // Collectors + representative line
  _buildBundleVisuals(bundleGroup, enriched, bwdSteps, fwdSteps, backStubEnds, forwStubEnds, bundlePath);
  if (bundleGroup.userData.visualWireCount != null) _applyVisualScale(bundleGroup);

  return true;
}

// Bundle'ın visual parçalarını (collectors + representative line) kurar
function _buildBundleVisuals(bundleGroup, enriched, bwdSteps, fwdSteps, backStubEnds, forwStubEnds, bundlePath) {
  if (bundlePath.length < 2) return;
  const p0 = bundlePath[0];
  const p1 = bundlePath[1];
  const entryDir = segDir(p0, p1);
  const entryPerp = { x: -entryDir.y, y: entryDir.x };
  const pN = bundlePath[bundlePath.length - 1];
  const pN1 = bundlePath[bundlePath.length - 2];
  const exitDir = segDir(pN1, pN);
  const exitPerp = { x: -exitDir.y, y: exitDir.x };

  const projectPerp = (p, perp) => p.x * perp.x + p.y * perp.y;
  const entryOffs = backStubEnds.map(p => projectPerp(p, entryPerp));
  const exitOffs = forwStubEnds.map(p => projectPerp(p, exitPerp));
  const entryHalfSpan = (Math.max(...entryOffs) - Math.min(...entryOffs)) / 2 + COLLECTOR_PAD;
  const exitHalfSpan = (Math.max(...exitOffs) - Math.min(...exitOffs)) / 2 + COLLECTOR_PAD;
  const entryPerpCenter = (Math.max(...entryOffs) + Math.min(...entryOffs)) / 2;
  const exitPerpCenter = (Math.max(...exitOffs) + Math.min(...exitOffs)) / 2;

  // Collector bilgisini sakla — scaleBundleVisuals rebuild sonrası re-apply için kullanır
  bundleGroup.userData.collectorEntryInfo = {
    anchor: { x: p0.x, y: p0.y }, dir: { x: entryDir.x, y: entryDir.y },
    perp: { x: entryPerp.x, y: entryPerp.y }, halfSpan: entryHalfSpan, wireCount: enriched.length,
    entryPerpCenter,
  };
  bundleGroup.userData.collectorExitInfo = {
    anchor: { x: pN.x, y: pN.y }, dir: { x: exitDir.x, y: exitDir.y },
    perp: { x: exitPerp.x, y: exitPerp.y }, halfSpan: exitHalfSpan, wireCount: enriched.length,
    exitPerpCenter,
  };

  // Wire rengini bundle'a dahil ilk wire'ın material'inden al — collector ve
  // rep line wire'larla aynı renkte çizilir, görsel uyum sağlanır.
  let bundleColor = BUNDLE_COLOR_REPRESENT;
  for (const e of enriched) {
    const m = e.wire?.material;
    if (m && m.color && typeof m.color.getHex === 'function') {
      bundleColor = m.color.getHex();
      break;
    }
  }

  const entryCollector = createCollectorGroup(entryDir, entryPerp, p0, entryHalfSpan, +1, bundleColor);
  const exitCollector = createCollectorGroup(exitDir, exitPerp, pN, exitHalfSpan, -1, bundleColor);
  entryCollector.userData = { isBundlePart: true, role: 'collector-entry' };
  exitCollector.userData = { isBundlePart: true, role: 'collector-exit' };
  bundleGroup.add(entryCollector);
  bundleGroup.add(exitCollector);

  // Rep line: cluster aralığı kadar her iki uçtan trim et → wire'lar 1. toplayıcı
  // çizgisinde biter, rep line 3. (en içteki) toplayıcı çizgisinden başlar.
  // Aradaki 2. çizgi görsel olarak boş kalır — kabloyu birleştirme sembolü.
  const COLLECTOR_INSET = (COLLECTOR_COUNT - 1) * COLLECTOR_SPACING;
  const repPath = trimPathFromEnds(bundlePath, COLLECTOR_INSET, COLLECTOR_INSET);
  if (repPath.length >= 2) {
    const repLine = createRepresentativePolyline(repPath, bundleColor, bundleGroup.userData?.id);
    // Slice sınırı kısıtlaması: gizmo sürüklemede ilk/son kenar slice dışına çıkmasın
    repLine.userData._bundleConstraintEntry = {
      perpX: entryPerp.x, perpY: entryPerp.y,
      perpMin: entryPerpCenter - entryHalfSpan,
      perpMax: entryPerpCenter + entryHalfSpan,
    };
    repLine.userData._bundleConstraintExit = {
      perpX: exitPerp.x, perpY: exitPerp.y,
      perpMin: exitPerpCenter - exitHalfSpan,
      perpMax: exitPerpCenter + exitHalfSpan,
    };
    bundleGroup.add(repLine);
  }
}

// ─── Visual Scale (görsel genişlik kontrolü) ─────────────────────────────────

// Sadece ENTRY (panel çıkışı) collector'ını visualWireCount'a göre ölçekler.
// Exit collector ve wire verisine dokunmaz.
function _applyVisualScale(bundleGroup) {
  const entryInfo = bundleGroup.userData.collectorEntryInfo;
  const vc = bundleGroup.userData.visualWireCount;
  if (!entryInfo || vc == null) return;

  const actualCount = entryInfo.wireCount;
  if (!actualCount || actualCount < 2) return;

  const clamped = Math.max(2, Math.min(actualCount, vc));
  const scale = (clamped - 1) / (actualCount - 1);

  // Rengi entry collector veya rep line'dan al
  let bundleColor = BUNDLE_COLOR_REPRESENT;
  for (const c of bundleGroup.children) {
    if (c.userData?.role === 'collector-entry') {
      const fl = c.children?.[0];
      if (fl?.material?.color?.getHex) { bundleColor = fl.material.color.getHex(); break; }
    }
    if (c.isLine && c.userData?.isBundleRep) {
      const col = c.material?.color?.getHex?.();
      if (col != null) { bundleColor = col; break; }
    }
  }

  // Sadece eski ENTRY collector'ı kaldır; exit dokunulmaz
  const toRemove = bundleGroup.children.filter(c => c.userData?.role === 'collector-entry');
  for (const c of toRemove) {
    bundleGroup.remove(c);
    c.traverse(obj => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material?.dispose) obj.material.dispose();
    });
  }

  // Seçili wirelerin gerçek perp merkezi varsa anchor'ı kaydır
  const visualEntry = bundleGroup.userData.visualEntryPerp;
  let newAnchor = { x: entryInfo.anchor.x, y: entryInfo.anchor.y };
  let scaledEntry;
  if (visualEntry) {
    const origCenter = entryInfo.entryPerpCenter ??
      (entryInfo.anchor.x * entryInfo.perp.x + entryInfo.anchor.y * entryInfo.perp.y);
    const perpShift = visualEntry.center - origCenter;
    newAnchor = {
      x: entryInfo.anchor.x + entryInfo.perp.x * perpShift,
      y: entryInfo.anchor.y + entryInfo.perp.y * perpShift,
    };
    scaledEntry = visualEntry.halfSpan;
  } else {
    scaledEntry = (entryInfo.halfSpan - COLLECTOR_PAD) * scale + COLLECTOR_PAD;
  }
  const entryCollector = createCollectorGroup(entryInfo.dir, entryInfo.perp, newAnchor, scaledEntry, +1, bundleColor);
  entryCollector.userData = { isBundlePart: true, role: 'collector-entry' };
  bundleGroup.add(entryCollector);
}

// Bundle collector'larını visualWireCount'a göre görsel olarak ölçekler.
// Entry stub'ları yeniden oluşturur: seçilmemiş wireler gizlenir.
// visualWireCount: 2 .. actualWireCount arası tam sayı.
export function scaleBundleVisuals(scene, bundleGroup, visualWireCount) {
  if (!bundleGroup || bundleGroup.userData?.type !== 'wireBundle') return false;
  bundleGroup.userData.visualWireCount = visualWireCount;
  const backStubLen = bundleGroup.userData.backStubLen ?? STUB_LEN;
  const forwStubLen = bundleGroup.userData.forwStubLen ?? STUB_LEN;
  rebuildBundleWithStubs(scene, bundleGroup, backStubLen, forwStubLen);
  return true;
}

export function createBundleFromRect(scene, rect) {
  const candidates = collectCandidateWires(scene, rect);
  if (candidates.length < 2) {
    console.warn('[Bundle] Rect içinde 2+ wire bulunamadı.');
    return null;
  }

  const grp = selectParallelGroup(candidates);
  if (grp.items.length < 2) {
    console.warn('[Bundle] Paralel grup çok küçük.');
    return null;
  }

  // Her wire için forward + backward segment listleri
  const enriched = grp.items.map(it => ({
    ...it,
    forwSegs: getForwardSegs(it.crossing),
    backSegs: getBackwardSegs(it.crossing),
  }));

  let fwdSteps = lockstepWalk(enriched.map(e => e.forwSegs));
  let bwdSteps = lockstepWalk(enriched.map(e => e.backSegs));
  // Komponentlerden (buat/priz/lamba/anahtar) önce bundle'ı kıs: bundle
  // kesinlikle bir komponentten önce bitsin, collector da 20 birim öncesinde
  // oluşsun.
  ({ fwdSteps, bwdSteps } = limitStepsByComponents(scene, enriched, fwdSteps, bwdSteps));

  // En azından bir yönde bir adım atamadıysak rect'i kullan
  if (fwdSteps === 0 && bwdSteps === 0) {
    console.warn('[Bundle] Lockstep walk başarısız; sadece rect içinde kalınacak.');
  }

  // Her wire için kendi "stub end" noktalarını hesaplayıp ortalamalarını
  // (midline) temsil çizgisi ve collector yerleşimi için kullanacağız.
  // Backward stub end: her wire için backward extreme'den STUB_LEN kadar
  // forward (rect'e doğru) yürüdüğümüz nokta.
  const backStubEnds = enriched.map(e => computeBackwardStubEnd(e.crossing, bwdSteps, STUB_LEN));
  const forwStubEnds = enriched.map(e => computeForwardStubEnd(e.crossing, fwdSteps, STUB_LEN));

  // Bundle midline path — her wire için backStubEnd'den forwStubEnd'e kadar
  // wire'ın polyline'ından clip'lenir, ardından ortalama alınır.
  const bundlePath = buildBundleMidlinePath(enriched, backStubEnds, forwStubEnds);

  // Snapshot + truncate
  const bundleId = generateUniqueId('wireBundle');
  const originalGeometries = enriched.map(it => ({
    wireId: it.wire.userData?.id,
    uuid: it.wire.uuid,
    positions: cloneGeometryPositions(it.wire),
    origVisible: it.wire.visible,
  }));
  const extraLines = [];
  for (const it of enriched) {
    const extra = truncateWireWithSteps(it.wire, it.crossing, bwdSteps, fwdSteps);
    if (extra) extraLines.push({ wireUuid: it.wire.uuid, extra });
  }

  // ─── Wire markerları gizle ─────────────────────────────────────
  // Her bundled wire için: orijinal wire path üzerinde bundle zone'unda
  // (rect içi + ilgili forwSegs/backSegs) olan wireMarker'ları bul ve gizle.
  const hiddenMarkers = hideWireMarkersInBundle(scene, enriched, bwdSteps, fwdSteps);

  const bundleGroup = new THREE.Group();
  bundleGroup.name = 'wireBundle';
  const _wireIdsRect = enriched.map(i => i.wire.userData?.id).filter(Boolean);
  bundleGroup.userData = {
    type: 'wireBundle',
    id: bundleId,
    wireIds: _wireIdsRect,
    originalGeometries,
    extraLineUuids: extraLines.map(x => x.extra.uuid),
    hiddenMarkerUuids: hiddenMarkers.map(m => m.uuid),
    rect: { ...rect },              // M-drag rebuild için rect'i sakla
    backStubLen: STUB_LEN,           // Şu anki stub uzunlukları
    forwStubLen: STUB_LEN,
    // Phase 1: bundle'ın panel-side exit index aralığı. Tek wire swap'lerinde
    // bu aralığa girilmesini engellemek + bundle grup-drag için kullanılır.
    panelExitRange: computeBundlePanelExitRange(scene, _wireIdsRect),
  };

  // Collectors at two ends of the bundle path
  // Entry (backward extreme) + exit (forward extreme)
  if (bundlePath.length >= 2) {
    const p0 = bundlePath[0];
    const p1 = bundlePath[1];
    const entryDir = segDir(p0, p1);
    const entryPerp = { x: -entryDir.y, y: entryDir.x };

    const pN = bundlePath[bundlePath.length - 1];
    const pN1 = bundlePath[bundlePath.length - 2];
    const exitDir = segDir(pN1, pN);
    const exitPerp = { x: -exitDir.y, y: exitDir.x };

    // Collector yarı uzunluğu = max wire perpendicular offset range
    const projectPerp = (p, perp) => p.x * perp.x + p.y * perp.y;
    const entryOffs = enriched.map(e => projectPerp(
      { x: (bwdSteps === 0 ? e.crossing.enterPoint.x : e.backSegs[bwdSteps - 1].end.x),
        y: (bwdSteps === 0 ? e.crossing.enterPoint.y : e.backSegs[bwdSteps - 1].end.y) },
      entryPerp
    ));
    const exitOffs = enriched.map(e => projectPerp(
      { x: (fwdSteps === 0 ? e.crossing.exitPoint.x : e.forwSegs[fwdSteps - 1].end.x),
        y: (fwdSteps === 0 ? e.crossing.exitPoint.y : e.forwSegs[fwdSteps - 1].end.y) },
      exitPerp
    ));
    const entryHalfSpan = (Math.max(...entryOffs) - Math.min(...entryOffs)) / 2 + COLLECTOR_PAD;
    const exitHalfSpan = (Math.max(...exitOffs) - Math.min(...exitOffs)) / 2 + COLLECTOR_PAD;
    const entryPerpCenter = (Math.max(...entryOffs) + Math.min(...entryOffs)) / 2;
    const exitPerpCenter = (Math.max(...exitOffs) + Math.min(...exitOffs)) / 2;

    bundleGroup.userData.collectorEntryInfo = {
      anchor: { x: p0.x, y: p0.y }, dir: { x: entryDir.x, y: entryDir.y },
      perp: { x: entryPerp.x, y: entryPerp.y }, halfSpan: entryHalfSpan, wireCount: enriched.length,
      entryPerpCenter,
    };
    bundleGroup.userData.collectorExitInfo = {
      anchor: { x: pN.x, y: pN.y }, dir: { x: exitDir.x, y: exitDir.y },
      perp: { x: exitPerp.x, y: exitPerp.y }, halfSpan: exitHalfSpan, wireCount: enriched.length,
      exitPerpCenter,
    };

    let bundleColor = BUNDLE_COLOR_REPRESENT;
    for (const e of enriched) {
      const m = e.wire?.material;
      if (m && m.color && typeof m.color.getHex === 'function') {
        bundleColor = m.color.getHex();
        break;
      }
    }

    const entryCollector = createCollectorGroup(entryDir, entryPerp, p0, entryHalfSpan, +1, bundleColor);
    const exitCollector = createCollectorGroup(exitDir, exitPerp, pN, exitHalfSpan, -1, bundleColor);
    entryCollector.userData = { isBundlePart: true, role: 'collector-entry' };
    exitCollector.userData = { isBundlePart: true, role: 'collector-exit' };
    bundleGroup.add(entryCollector);
    bundleGroup.add(exitCollector);

    // Rep line cluster aralığı kadar trim et → 3. (en içteki) toplayıcı çizgisinden başlar
    const COLLECTOR_INSET_RECT = (COLLECTOR_COUNT - 1) * COLLECTOR_SPACING;
    const repPathRect = trimPathFromEnds(bundlePath, COLLECTOR_INSET_RECT, COLLECTOR_INSET_RECT);
    if (repPathRect.length >= 2) {
      const repLineRect = createRepresentativePolyline(repPathRect, bundleColor, bundleGroup.userData?.id);
      repLineRect.userData._bundleConstraintEntry = {
        perpX: entryPerp.x, perpY: entryPerp.y,
        perpMin: entryPerpCenter - entryHalfSpan,
        perpMax: entryPerpCenter + entryHalfSpan,
      };
      repLineRect.userData._bundleConstraintExit = {
        perpX: exitPerp.x, perpY: exitPerp.y,
        perpMin: exitPerpCenter - exitHalfSpan,
        perpMax: exitPerpCenter + exitHalfSpan,
      };
      bundleGroup.add(repLineRect);
    }
  }

  scene.add(bundleGroup);
  return bundleGroup;
}

// İki "slice" hattıyla bundle oluşturur. mergeSlice ve openSlice her biri
// {a, b} formunda iki nokta tanımlı doğru parçası. Wire'lar her iki hattı da
// kesmek zorundadır; bundle iki hat arasında kalır, otomatik genişletilmez.
export function createBundleFromTwoSlices(scene, mergeSlice, openSlice) {
  const candidates = collectCandidateWiresBySlices(
    scene, mergeSlice.a, mergeSlice.b, openSlice.a, openSlice.b
  );
  if (candidates.length < 2) {
    console.warn('[Bundle/slices] Her iki hattı kesen 2+ wire bulunamadı.');
    return null;
  }

  const grp = selectParallelGroup(candidates);
  if (grp.items.length < 2) {
    console.warn('[Bundle/slices] Paralel grup çok küçük.');
    return null;
  }

  const enriched = grp.items.map(it => ({
    ...it,
    forwSegs: getForwardSegs(it.crossing),
    backSegs: getBackwardSegs(it.crossing),
  }));

  const fwdSteps = 0;
  const bwdSteps = 0;
  const safeBack = clampStubLen(enriched, STUB_LEN);
  const safeForw = clampStubLen(enriched, STUB_LEN);

  // 1. iterasyon: ilk backStubEnds & bundlePath
  let backStubEnds = enriched.map(e => computeBackwardStubEnd(e.crossing, bwdSteps, safeBack));
  let forwStubEnds = enriched.map(e => computeForwardStubEnd(e.crossing, fwdSteps, safeForw));
  let bundlePath = buildBundleMidlinePath(enriched, backStubEnds, forwStubEnds);

  // Hizalama: tüm wire'ları ilk toplayıcı çizgisinde topla. Köşe-geçen bundle'larda
  // da güvenli — projeksiyon entry/exit segmentine sınırlı.
  let aligned = alignStubsToCollectorLine(enriched, backStubEnds, forwStubEnds, bundlePath);
  backStubEnds = aligned.backStubEnds;
  forwStubEnds = aligned.forwStubEnds;
  bundlePath = buildBundleMidlinePath(enriched, backStubEnds, forwStubEnds);

  const bundleId = generateUniqueId('wireBundle');
  const originalGeometries = enriched.map(it => ({
    wireId: it.wire.userData?.id,
    uuid: it.wire.uuid,
    positions: cloneGeometryPositions(it.wire),
    origVisible: it.wire.visible,
  }));
  const extraLines = [];
  for (let i = 0; i < enriched.length; i++) {
    const it = enriched[i];
    const bStub = aligned.alignedBackStubs ? aligned.alignedBackStubs[i] : safeBack;
    const fStub = aligned.alignedForwStubs ? aligned.alignedForwStubs[i] : safeForw;
    const extra = truncateWireWithSteps(it.wire, it.crossing, bwdSteps, fwdSteps, bStub, fStub);
    if (extra) extraLines.push({ wireUuid: it.wire.uuid, extra });
  }
  const hiddenMarkers = hideWireMarkersInBundle(scene, enriched, bwdSteps, fwdSteps);

  const bundleGroup = new THREE.Group();
  bundleGroup.name = 'wireBundle';
  const _wireIdsSlice = enriched.map(i => i.wire.userData?.id).filter(Boolean);
  bundleGroup.userData = {
    type: 'wireBundle',
    id: bundleId,
    wireIds: _wireIdsSlice,
    originalGeometries,
    extraLineUuids: extraLines.map(x => x.extra.uuid),
    hiddenMarkerUuids: hiddenMarkers.map(m => m.uuid),
    slices: {
      merge: { a: { x: mergeSlice.a.x, y: mergeSlice.a.y }, b: { x: mergeSlice.b.x, y: mergeSlice.b.y } },
      open:  { a: { x: openSlice.a.x,  y: openSlice.a.y  }, b: { x: openSlice.b.x,  y: openSlice.b.y  } },
    },
    // Sabit referans güzergâh — slice-slide drag bu yola projekte edilir.
    // Bundle wire setiyle birlikte oluşturulur; rebuild'le değişmez.
    guidePath: undefined,
    // guidePath üzerindeki köşe sample index'leri — slide drag snap target.
    guideCorners: undefined,
    guideCornerArcs: undefined, // arc-length cinsinden köşe konumları
    guideBridgeZones: undefined, // her köşe için wire-vertex bazlı transition zone
    innerCorners: undefined, // her köşe için iç bükey wire vertex + yön bilgisi
    wireCount: enriched.length, // X = (N-1)/2*5+5 hesabı için
    backStubLen: safeBack,
    forwStubLen: safeForw,
    panelExitRange: computeBundlePanelExitRange(scene, _wireIdsSlice),
  };
  // guidePath + corners + arc'ları + bridge zone'ları + inner corners tek hesapla
  const _gp = buildGuidePath(enriched);
  const _gc = computeGuideCorners(_gp);
  bundleGroup.userData.guidePath = _gp;
  bundleGroup.userData.guideCorners = _gc;
  bundleGroup.userData.guideCornerArcs = computeCornerArcs(_gp, _gc);
  bundleGroup.userData.guideBridgeZones = computeCornerBridgeZones(_gp, _gc, enriched);
  bundleGroup.userData.innerCorners = computeInnerCornerData(enriched);

  _buildBundleVisuals(bundleGroup, enriched, bwdSteps, fwdSteps, backStubEnds, forwStubEnds, bundlePath);
  if (bundleGroup.userData.visualWireCount != null) _applyVisualScale(bundleGroup);

  scene.add(bundleGroup);
  return bundleGroup;
}

// İki tıklama noktası arasında bundle oluşturur. Bundle KESİNLİKLE iki nokta
// arasında kalır; otomatik genişletilmez. Adaylar hem A hem de B noktasının
// `tol` birim çevresinden geçen + click1→click2 yönüne paralel wire'lardır.
export function createBundleFromTwoPoints(scene, pA, pB) {
  let candidates = collectCandidateWiresByTwoPoints(scene, pA, pB);
  if (candidates.length < 2) {
    console.warn('[Bundle/2pt] İki noktanın tolerans çevresinde 2+ wire bulunamadı.');
    return null;
  }

  // Click yönüne paralel olmayan wire'ları ele — kullanıcı yatay click yaparken
  // dikey wire'ların bundle'lanmasını önler. Tolerance: ±25° (paralel + ters).
  const clickDir = segDir(pA, pB);
  const DIR_TOL_DEG = 25;
  candidates = candidates.filter(c => dirsMatch(c.crossing.entryDir, clickDir, DIR_TOL_DEG));
  if (candidates.length < 2) {
    console.warn('[Bundle/2pt] Click yönüne paralel wire 2+ bulunamadı.');
    return null;
  }

  const grp = selectParallelGroup(candidates);
  if (grp.items.length < 2) {
    console.warn('[Bundle/2pt] Paralel grup çok küçük.');
    return null;
  }

  const enriched = grp.items.map(it => ({
    ...it,
    forwSegs: getForwardSegs(it.crossing),
    backSegs: getBackwardSegs(it.crossing),
  }));

  // İki-nokta akışında otomatik genişleme yok
  const fwdSteps = 0;
  const bwdSteps = 0;

  const safeBack = clampStubLen(enriched, STUB_LEN);
  const safeForw = clampStubLen(enriched, STUB_LEN);

  let backStubEnds = enriched.map(e => computeBackwardStubEnd(e.crossing, bwdSteps, safeBack));
  let forwStubEnds = enriched.map(e => computeForwardStubEnd(e.crossing, fwdSteps, safeForw));
  let bundlePath = buildBundleMidlinePath(enriched, backStubEnds, forwStubEnds);

  // Hizalama: tüm wire'lar ilk toplayıcı çizgisinde toplanır.
  let aligned2pt = alignStubsToCollectorLine(enriched, backStubEnds, forwStubEnds, bundlePath);
  backStubEnds = aligned2pt.backStubEnds;
  forwStubEnds = aligned2pt.forwStubEnds;
  bundlePath = buildBundleMidlinePath(enriched, backStubEnds, forwStubEnds);

  const bundleId = generateUniqueId('wireBundle');
  const originalGeometries = enriched.map(it => ({
    wireId: it.wire.userData?.id,
    uuid: it.wire.uuid,
    positions: cloneGeometryPositions(it.wire),
    origVisible: it.wire.visible,
  }));
  const extraLines = [];
  for (let i = 0; i < enriched.length; i++) {
    const it = enriched[i];
    const bStub = aligned2pt.alignedBackStubs ? aligned2pt.alignedBackStubs[i] : safeBack;
    const fStub = aligned2pt.alignedForwStubs ? aligned2pt.alignedForwStubs[i] : safeForw;
    const extra = truncateWireWithSteps(it.wire, it.crossing, bwdSteps, fwdSteps, bStub, fStub);
    if (extra) extraLines.push({ wireUuid: it.wire.uuid, extra });
  }
  const hiddenMarkers = hideWireMarkersInBundle(scene, enriched, bwdSteps, fwdSteps);

  const bundleGroup = new THREE.Group();
  bundleGroup.name = 'wireBundle';
  const _wireIds2pt = enriched.map(i => i.wire.userData?.id).filter(Boolean);
  bundleGroup.userData = {
    type: 'wireBundle',
    id: bundleId,
    wireIds: _wireIds2pt,
    originalGeometries,
    extraLineUuids: extraLines.map(x => x.extra.uuid),
    hiddenMarkerUuids: hiddenMarkers.map(m => m.uuid),
    points: { a: { x: pA.x, y: pA.y }, b: { x: pB.x, y: pB.y } }, // M-drag rebuild için
    backStubLen: safeBack,
    forwStubLen: safeForw,
    panelExitRange: computeBundlePanelExitRange(scene, _wireIds2pt),
  };

  _buildBundleVisuals(bundleGroup, enriched, bwdSteps, fwdSteps, backStubEnds, forwStubEnds, bundlePath);
  if (bundleGroup.userData.visualWireCount != null) _applyVisualScale(bundleGroup);

  scene.add(bundleGroup);
  return bundleGroup;
}

// ─── Expand ──────────────────────────────────────────────────────────────────

export function expandBundle(scene, bundleOrId) {
  const bundle = typeof bundleOrId === 'string'
    ? findBundleById(scene, bundleOrId)
    : bundleOrId;
  if (!bundle || bundle.userData?.type !== 'wireBundle') return false;

  // Extra line'ları sil
  const extraUuids = new Set(bundle.userData.extraLineUuids || []);
  if (extraUuids.size > 0) {
    const toRemove = [];
    scene.traverse(obj => { if (extraUuids.has(obj.uuid)) toRemove.push(obj); });
    for (const obj of toRemove) {
      obj.parent?.remove(obj);
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material?.dispose) obj.material.dispose();
    }
  }

  // Gizlenen wire marker'ları geri aç
  const markerUuids = new Set(bundle.userData.hiddenMarkerUuids || []);
  if (markerUuids.size > 0) {
    scene.traverse(obj => {
      if (markerUuids.has(obj.uuid)) obj.visible = true;
    });
  }

  // Orijinal geometrileri geri yükle
  for (const o of (bundle.userData.originalGeometries || [])) {
    let wire = null;
    scene.traverse(obj => {
      if (wire) return;
      if (obj.uuid === o.uuid) wire = obj;
      else if (obj.userData?.id === o.wireId && isBundleableWire(obj)) wire = obj;
    });
    if (!wire) continue;
    const arr = new Float32Array(o.positions);
    wire.geometry.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    wire.geometry.attributes.position.needsUpdate = true;
    wire.geometry.computeBoundingBox();
    wire.geometry.computeBoundingSphere();
    if (typeof wire.computeLineDistances === 'function') wire.computeLineDistances();
    wire.visible = o.origVisible !== false;
  }

  scene.remove(bundle);
  bundle.traverse(obj => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material?.dispose) obj.material.dispose();
  });
  return true;
}

function findBundleById(scene, id) {
  let found = null;
  scene.traverse(obj => {
    if (found) return;
    if (obj.userData?.type === 'wireBundle' && obj.userData?.id === id) found = obj;
  });
  return found;
}

export function findBundleContaining(scene, wireId) {
  let found = null;
  scene.traverse(obj => {
    if (found) return;
    if (obj.userData?.type !== 'wireBundle') return;
    if ((obj.userData.wireIds || []).includes(wireId)) found = obj;
  });
  return found;
}

export function listBundles(scene) {
  const out = [];
  scene.traverse(obj => {
    if (obj.userData?.type === 'wireBundle') out.push(obj);
  });
  return out;
}
